"use client";

import { useEffect, useState, type FormEvent } from "react";
import Link from "next/link";
import { useConvexAuth, useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { isOrgAdmin } from "@shared/auth/roles";
import { closeSignInModal } from "@/lib/auth/sign-in-modal-store";
import { Button } from "@/components/ui/button";
import { AuthField } from "./steps/auth-field";

interface DetailsValues {
  firstName: string;
  lastName: string;
  company: string;
}

function useCurrentAccount() {
  const auth = useConvexAuth();
  const current = useQuery(
    api.companies.queries.getCurrentUserWithCompany,
    auth.isAuthenticated ? {} : "skip",
  );
  return {
    current,
    isSignedOut: !auth.isLoading && !auth.isAuthenticated,
  };
}

function useSaveDetails(
  current: ReturnType<typeof useCurrentAccount>["current"],
  canNameCompany: boolean,
  onDone: () => void,
) {
  const updateUser = useMutation(api.users.mutations.updateProfile);
  const updateCompany = useMutation(api.companies.mutations.profile.updateProfile);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const save = async (values: DetailsValues) => {
    const firstName = values.firstName.trim();
    const lastName = values.lastName.trim();
    const companyName = values.company.trim();
    if (!firstName || !lastName || (canNameCompany && !companyName)) {
      setError("Enter your first name, last name and company.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await updateUser({ firstName, lastName });
      const company = current?.company;
      if (canNameCompany && company && companyName !== company.name) {
        const result = await updateCompany({ companyId: company._id, name: companyName });
        if (!result.success) throw new Error(result.error ?? "Company not saved");
      }
      onDone();
    } catch {
      setError("We couldn't save your details. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return { error, isSaving, save };
}

function CompanyField(props: {
  company: { name: string } | null;
  canNameCompany: boolean;
  value: string;
  onChange: (value: string) => void;
}) {
  if (props.canNameCompany) {
    return (
      <AuthField label="Company" autoComplete="organization" value={props.value} onChange={props.onChange} />
    );
  }
  if (props.company) {
    return <p className="text-sm text-muted-foreground">You&apos;re joining {props.company.name}.</p>;
  }
  return (
    <p role="status" className="text-sm text-muted-foreground">
      Company setup isn&apos;t finished —{" "}
      <Link href="/setup" onClick={closeSignInModal} className="underline">finish in setup</Link>.
    </p>
  );
}

function useDetailsValues(current: ReturnType<typeof useCurrentAccount>["current"]) {
  const [values, setValues] = useState<DetailsValues | null>(null);
  const shown: DetailsValues = values ?? {
    firstName: current?.user.firstName ?? "",
    lastName: current?.user.lastName ?? "",
    company: current?.company?.name ?? "",
  };
  const set = (key: keyof DetailsValues) => (value: string) =>
    setValues({ ...shown, [key]: value });
  return { shown, set };
}

export function AccountDetailsForm({
  onDone,
  onSignedOut,
}: {
  onDone: () => void;
  onSignedOut: () => void;
}) {
  const { current, isSignedOut } = useCurrentAccount();
  useEffect(() => {
    if (isSignedOut) onSignedOut();
  }, [isSignedOut, onSignedOut]);
  const company = current?.company ?? null;
  const canNameCompany = company !== null && isOrgAdmin(company.role);
  const details = useSaveDetails(current, canNameCompany, onDone);
  const { shown, set } = useDetailsValues(current);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void details.save(shown);
  };

  return (
    <form className="space-y-3" onSubmit={submit}>
      <div className="grid grid-cols-2 gap-3">
        <AuthField label="First name" autoComplete="given-name" value={shown.firstName} onChange={set("firstName")} />
        <AuthField label="Last name" autoComplete="family-name" value={shown.lastName} onChange={set("lastName")} />
      </div>
      <CompanyField company={company} canNameCompany={canNameCompany} value={shown.company} onChange={set("company")} />
      {details.error ? (
        <p role="alert" className="text-sm text-destructive-strong">
          {details.error}
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground">
        By continuing you agree to the{" "}
        <Link href="/terms-conditions" target="_blank" rel="noopener noreferrer" className="underline">Terms</Link> and{" "}
        <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</Link>.
      </p>
      <Button type="submit" variant="primary" className="w-full" disabled={details.isSaving || company === null}>
        {details.isSaving ? "Saving…" : "Agree and continue"}
      </Button>
    </form>
  );
}
